import { useCallback, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { ShortcutHelpModal } from './ShortcutHelpModal';
import { useFocusTrap } from '@/core/keyboard';
import { usePosBilling } from '@/pages/pos/PosBillingContext';
import type { PaymentMethod } from '@/types/enums';
import { useAppSelector } from '@/hooks/useAppSelector';
import { notify } from '@/core/toast/notify';

const BASE_OPTIONS: { value: string; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CARD', label: 'Card' },
  { value: 'WALLET', label: 'Wallet' },
  { value: 'CREDIT', label: 'Credit (account)' },
];

export function PosBillingModals() {
  const posCustomer = useAppSelector((s) => s.posCustomer);
  const {
    paymentOpen,
    setPaymentOpen,
    discountOpen,
    setDiscountOpen,
    helpOpen,
    setHelpOpen,
    qtyEdit,
    setQtyEdit,
    paymentMethod,
    setPaymentMethod,
    paidAmount,
    setPaidAmount,
    billDiscount,
    setBillDiscount,
    couponCode,
    setCouponCode,
    loyaltyRedeem,
    setLoyaltyRedeem,
    checkoutGateways,
    gatewayProvider,
    setGatewayProvider,
    splitPayMode,
    setSplitPayMode,
    splitPayments,
    setSplitPayments,
    splitPaymentAllowed,
    processing,
    couponLoading,
    completeSale,
    applyBillDiscountFromField,
    applyCoupon,
    totals,
    fmt,
    customerIsWalkIn,
    selectedIndex,
    updateQty,
  } = usePosBilling();

  const paymentTrapRef = useFocusTrap(paymentOpen);
  const selectedCustomer = posCustomer.selected;

  const paymentOptions = useMemo(() => {
    const gatewayOpts = checkoutGateways.map((g) => ({
      value: `gw:${g.providerCode}`,
      label: g.displayName || g.providerCode,
    }));
    return [...BASE_OPTIONS, ...gatewayOpts];
  }, [checkoutGateways]);

  const selectValue = gatewayProvider ? `gw:${gatewayProvider}` : paymentMethod;

  const availableMethods = useMemo(
    () => paymentOptions.map((o) => o.value),
    [paymentOptions],
  );

  const applyPaymentSelection = useCallback(
    (val: string) => {
      if (val.startsWith('gw:')) {
        const code = val.slice(3);
        setGatewayProvider(code);
        if (code === 'RAZORPAY' || code === 'STRIPE' || code === 'PAYTM') {
          setPaymentMethod(code as PaymentMethod);
        } else {
          setPaymentMethod('UPI');
        }
        setPaidAmount(String(totals.total));
        return;
      }
      setGatewayProvider(null);
      const method = val as PaymentMethod;
      setPaymentMethod(method);
      if (method === 'WALLET') {
        setPaidAmount(String(totals.total));
        if (customerIsWalkIn) notify.error('Select a registered customer for wallet');
      } else if (method === 'CREDIT') {
        setPaidAmount(String(totals.total));
        if (customerIsWalkIn) notify.error('Select a credit-enabled customer');
      }
    },
    [
      customerIsWalkIn,
      setGatewayProvider,
      setPaidAmount,
      setPaymentMethod,
      totals.total,
    ],
  );

  useEffect(() => {
    if (!paymentOpen) return;
    const onPaymentKeys = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          void completeSale();
        }
        return;
      }
      const idx = availableMethods.indexOf(selectValue);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = availableMethods[(idx + 1) % availableMethods.length];
        applyPaymentSelection(next);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = availableMethods[(idx - 1 + availableMethods.length) % availableMethods.length];
        applyPaymentSelection(next);
      } else       if (e.key === 'Enter') {
        e.preventDefault();
        void completeSale();
        return;
      }
      const quickMethods = ['1', '2', '3', '4'] as const;
      const quickMap: Record<string, string> = {
        '1': 'CASH',
        '2': 'UPI',
        '3': 'CARD',
        '4': 'WALLET',
      };
      if (quickMethods.includes(e.key as (typeof quickMethods)[number]) && !splitPayMode) {
        e.preventDefault();
        applyPaymentSelection(quickMap[e.key]);
      }
    };
    window.addEventListener('keydown', onPaymentKeys);
    return () => window.removeEventListener('keydown', onPaymentKeys);
  }, [paymentOpen, selectValue, availableMethods, completeSale, applyPaymentSelection]);

  const autoAmount =
    paymentMethod === 'WALLET' ||
    paymentMethod === 'CREDIT' ||
    gatewayProvider != null;

  return (
    <>
      <Modal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        title="Payment"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPaymentOpen(false)}>
              Cancel (Esc)
            </Button>
            <Button onClick={() => void completeSale()} loading={processing}>
              Complete (Enter / Ctrl+Enter)
            </Button>
          </>
        }
      >
        <div ref={paymentTrapRef}>
          <p className="mb-4 text-3xl font-bold text-slate-900 dark:text-white">{fmt(totals.total)}</p>
          {splitPaymentAllowed && !gatewayProvider && (
            <label className="mb-3 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={splitPayMode}
                onChange={(e) => setSplitPayMode(e.target.checked)}
              />
              Split payment (multiple tenders)
            </label>
          )}
          {splitPayMode ? (
            <div className="space-y-3">
              {splitPayments.map((row, idx) => (
                <div key={idx} className="grid grid-cols-2 gap-2">
                  <Select
                    label={`Tender ${idx + 1}`}
                    options={BASE_OPTIONS}
                    value={row.paymentMethod}
                    onChange={(v) => {
                      const method = String(v) as PaymentMethod;
                      setSplitPayments((prev) =>
                        prev.map((p, i) => (i === idx ? { ...p, paymentMethod: method } : p)),
                      );
                    }}
                  />
                  <Input
                    label="Amount"
                    type="number"
                    min={0}
                    step="0.01"
                    value={String(row.amount)}
                    onChange={(e) => {
                      const amount = Number(e.target.value) || 0;
                      setSplitPayments((prev) =>
                        prev.map((p, i) => (i === idx ? { ...p, amount } : p)),
                      );
                    }}
                  />
                </div>
              ))}
              <p className="text-xs text-slate-500">
                Split total:{' '}
                {fmt(splitPayments.reduce((s, p) => s + Number(p.amount || 0), 0))} /{' '}
                {fmt(totals.total)}
              </p>
            </div>
          ) : (
            <Select
              label="Payment method (↑↓ or 1–4)"
              options={paymentOptions}
              value={selectValue}
              onChange={(v) => applyPaymentSelection(String(v))}
            />
          )}
          {paymentMethod === 'WALLET' && (
            <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
              Wallet: {fmt(selectedCustomer?.walletBalance ?? 0)} available
            </p>
          )}
          {paymentMethod === 'CREDIT' && (
            <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
              Credit: {fmt(selectedCustomer?.creditAvailable ?? 0)} available
              {selectedCustomer?.creditLimit != null &&
                ` · limit ${fmt(selectedCustomer.creditLimit)}`}
            </p>
          )}
          {gatewayProvider && (
            <p className="mt-2 text-sm text-primary-700 dark:text-primary-300">
              Opens {gatewayProvider} checkout after invoice is created.
            </p>
          )}
          <Input
            label="Amount received"
            type="number"
            min={0}
            step="0.01"
            value={paidAmount}
            onChange={(e) => setPaidAmount(e.target.value)}
            disabled={autoAmount}
            className="mt-4"
          />
          {!customerIsWalkIn && (
            <>
              <Input
                label="Loyalty points to redeem (1 pt = 1 unit off)"
                type="number"
                min={0}
                step="1"
                value={loyaltyRedeem}
                onChange={(e) => setLoyaltyRedeem(e.target.value)}
                className="mt-4"
              />
              <p className="mt-1 text-xs text-slate-500">
                Balance:{' '}
                {selectedCustomer?.loyaltyPointsBalance ?? selectedCustomer?.loyaltyPoints ?? 0} pts
              </p>
            </>
          )}
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <Input
              label="Bill discount"
              type="number"
              value={billDiscount}
              onChange={(e) => setBillDiscount(e.target.value)}
            />
            <div className="flex items-end gap-2">
              <Input
                label="Coupon"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                className="flex-1"
              />
              <Button variant="secondary" onClick={() => void applyCoupon()} loading={couponLoading}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={discountOpen}
        onClose={() => setDiscountOpen(false)}
        title="Bill discount"
        footer={
          <Button
            onClick={() => {
              applyBillDiscountFromField();
              setDiscountOpen(false);
            }}
          >
            Apply (Enter)
          </Button>
        }
      >
        <Input
          label="Discount amount"
          type="number"
          value={billDiscount}
          onChange={(e) => setBillDiscount(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              applyBillDiscountFromField();
              setDiscountOpen(false);
            }
          }}
        />
      </Modal>

      <Modal
        open={!!qtyEdit}
        onClose={() => setQtyEdit('')}
        title="Quantity"
        footer={
          <Button
            onClick={() => {
              updateQty(selectedIndex, Number(qtyEdit) || 1);
              setQtyEdit('');
            }}
          >
            OK
          </Button>
        }
      >
        <Input
          label="Quantity"
          type="number"
          min={0.001}
          step="0.001"
          value={qtyEdit}
          onChange={(e) => setQtyEdit(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              updateQty(selectedIndex, Number(qtyEdit) || 1);
              setQtyEdit('');
            }
          }}
        />
      </Modal>

      <ShortcutHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}
